// Pantalla «Mis clientes» (`/collections/customers`): el explorer de 3 columnas del mockup —filtros ·
// tabla · ficha—. Los datos salen de `fixtures.ts`: la cobranza todavía no tiene endpoints.
import { useCallback, useMemo, useState } from 'react';
import { mxDayOffset } from '@/lib/dateHelpers';
import { MockBoundary } from '@/shared/components/MockBoundary';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { StatsBar } from '@/shared/patterns/StatsBar';
import { createCustomersFixture } from './fixtures';
import {
    applyStatFilter,
    buildCustomerRows,
    computeCustomerStats,
    customersStatsItems,
    filterCustomers,
    formatMoneyCents,
    sortCustomers,
    sumArrearsCents,
} from './logic';
import { emptyCustomersFilters, type CustomersFilters, type CustomerStatKey } from './types';
import { CustomerDetail } from './components/CustomerDetail';
import { CustomersFiltersPanel } from './components/CustomersFiltersPanel';
import { CustomersTable } from './components/CustomersTable';

export function CustomersPage() {
    const [filters, setFilters] = useState<CustomersFilters>(emptyCustomersFilters);
    const [stat, setStat] = useState<CustomerStatKey>('all');
    const [pickedId, setPickedId] = useState<string | null>(null);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    // El fixture se ancla al día de hoy, como el `HOY` del mockup: los compromisos de hoy son de hoy.
    const rows = useMemo(() => buildCustomerRows(createCustomersFixture(mxDayOffset(0))), []);
    const stats = useMemo(() => computeCustomerStats(rows), [rows]);
    const visibleRows = useMemo(
        () => sortCustomers(filterCustomers(applyStatFilter(rows, stat), filters)),
        [rows, stat, filters],
    );

    // Sin elección propia se abre el primero de la lista, como el mockup al cargar.
    const selectedId = pickedId ?? (visibleRows.length > 0 ? visibleRows[0].id : null);
    const selected = visibleRows.find((row) => row.id === selectedId) ?? null;

    const handleFiltersChange = useCallback((next: CustomersFilters) => {
        setFilters(next);
        setPickedId(null);
    }, []);

    const handleStatChange = useCallback((next: CustomerStatKey) => {
        setStat(next);
        setPickedId(null);
    }, []);

    return (
        <ScreenFrame>
            <MockBoundary
                active
                label="Mock · sin backend"
                className="flex min-h-0 flex-1 flex-col"
            >
                <MasterDetailExplorer
                    storageKey="collections-customers"
                    filtersCollapsed={filtersCollapsed}
                    filters={
                        <CustomersFiltersPanel
                            filters={filters}
                            onChange={handleFiltersChange}
                            collapsed={filtersCollapsed}
                            onToggleCollapsed={() => {
                                setFiltersCollapsed((prev) => !prev);
                            }}
                        />
                    }
                    list={
                        <>
                            <StatsBar
                                items={customersStatsItems(stats)}
                                active={stat}
                                onChange={handleStatChange}
                            />
                            <div className="flex flex-none items-center gap-2 border-b border-border px-3 py-2 text-[11px] text-muted-foreground">
                                <span>{visibleRows.length} clientes</span>
                                <span className="flex-1" />
                                <span>
                                    Cartera total:{' '}
                                    <span className="font-semibold">
                                        {formatMoneyCents(sumArrearsCents(visibleRows))}
                                    </span>
                                </span>
                            </div>
                            <CustomersTable
                                rows={visibleRows}
                                selectedId={selectedId}
                                onSelect={setPickedId}
                            />
                        </>
                    }
                    detail={<CustomerDetail customer={selected} />}
                />
            </MockBoundary>
        </ScreenFrame>
    );
}
